import React, { useState } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ChecklistItem as ChecklistItemType } from '../../services/onboardingService';
import { useOnboarding } from '../OnboardingProvider';
import { CHECKLIST_TO_TOUR } from '../tours';

// Delay between navigating to the target page and kicking off the mini-tour.
// Gives React Router + page component time to mount so the tour's DOM anchors
// exist when react-joyride starts looking for them.
const TOUR_START_DELAY_MS = 400;

interface ChecklistItemProps {
  item: ChecklistItemType;
  active?: boolean;
}

export const ChecklistItem: React.FC<ChecklistItemProps> = ({ item, active }) => {
  const navigate = useNavigate();
  const { closeDrawer, update, startTour } = useOnboarding();
  const [saving, setSaving] = useState(false);

  const handleStart = () => {
    closeDrawer();
    navigate(item.actionHref);

    // Kick off the mini-tour once the target page has had a chance to render.
    const miniTourId = CHECKLIST_TO_TOUR[item.id];
    if (miniTourId) {
      window.setTimeout(() => startTour(miniTourId), TOUR_START_DELAY_MS);
    }
  };

  const handleMarkAsDone = async () => {
    setSaving(true);
    try {
      await update({ manuallyCompletedItems: [item.id] });
    } catch {
      // The provider's onError snackbar already surfaces the failure — this
      // catch just prevents an unhandled rejection on top of it.
    } finally {
      setSaving(false);
    }
  };

  const isCompleted = item.completed;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        bgcolor: active && !isCompleted ? '#FDF8F2' : 'transparent',
        border: active && !isCompleted ? '1px solid #E8D7BF' : '1px solid transparent',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover': !isCompleted
          ? {
              bgcolor: '#FDF8F2',
              borderColor: '#E8D7BF',
            }
          : {},
      }}
    >
      <Box sx={{ pt: 0.25 }}>
        {isCompleted ? (
          <CheckCircle sx={{ color: '#2F7D32', fontSize: 22 }} />
        ) : (
          <RadioButtonUnchecked sx={{ color: '#C4A478', fontSize: 22 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: isCompleted ? '#6B6050' : '#3B2E1A',
            textDecoration: isCompleted ? 'line-through' : 'none',
            lineHeight: 1.35,
            mb: 0.25,
          }}
        >
          {item.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#6B6050',
            lineHeight: 1.45,
            fontSize: '0.75rem',
          }}
        >
          {item.description}
        </Typography>
      </Box>
      {!isCompleted && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            alignSelf: 'center',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleStart}
            variant="outlined"
            size="small"
            disabled={saving}
            sx={{
              borderColor: '#8B4513',
              color: '#8B4513',
              borderRadius: 16,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              minWidth: 64,
              px: 1.5,
              py: 0.25,
              '&:hover': {
                borderColor: '#6B4F2E',
                bgcolor: 'rgba(139,69,19,0.08)',
              },
            }}
          >
            Start
          </Button>
          <Tooltip
            title="Mark done without doing the step — use this if it doesn't apply to your clinic."
            arrow
            placement="left"
          >
            <Button
              onClick={handleMarkAsDone}
              variant="text"
              size="small"
              disabled={saving}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.7rem',
                color: '#8B6B4A',
                minWidth: 0,
                px: 0.75,
                py: 0,
                lineHeight: 1.2,
                '&:hover': {
                  color: '#6B4F2E',
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Not needed
            </Button>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
